import { css } from 'styled-components';
import { useCallback, useEffect, useMemo } from 'react';
import { GlobalToolbarButton } from './GlobalToolbarButton';
import { GlobalToolbarDivider } from './GlobalToolbarDivider';
import { ICON_NAMES } from './GlobalToolbarIcon';
import { useComponentProps, useComponentState, useInvokeFunction, useToggleComponent } from 'navigation-components/platform/hooks';
import { CHATSPOT_SIDEBAR_COMPONENT, GLOBAL_SEARCH_COMPONENT, chatspotSidebarComponentDef } from 'navigation-components/config';
import setRootStyleProperty from '../../../utils/setRootStyleProperty';
import { isMac } from '../../../utils/isMac';
import { captureException } from '../../../utils/sentryAPI';
import useEffectOnce from '../../../hooks/useEffectOnce';
import { RESPONSIVE_TOOLBAR } from '../../../constants/gates';
import { useIsUngated } from '../../../hooks/useIsUngated';
import useMedia from '../../../hooks/useMedia';
import { BREAKPOINT_MEDIUM } from 'nav-meta/constants';
import { useTheme } from 'foundations-theming-specialty/utils';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getAdditionalStyles = theme => css(["&&& > .icon-text-key{color:", " !important;}margin:0 0 0 6px !important;max-height:32px;"], theme.color['global-shell-text-copilot-default']);

// The keyboard shortcut to toggle Copilot is Cmd + . on Mac and CTRL + . on Windows
const isToggleCopilotShortcut = event => event && event.altKey === false && event.ctrlKey === !isMac() && event.metaKey === isMac() && event.shiftKey === false && event.key === '.';
export const ActivateCopilotButton = () => {
  const {
    toggle
  } = useToggleComponent(CHATSPOT_SIDEBAR_COMPONENT);
  const theme = useTheme();
  const {
    isOpen: isCopilotOpen
  } = useComponentState(CHATSPOT_SIDEBAR_COMPONENT);
  const {
    isOpen: isSearchOpen
  } = useComponentState(GLOBAL_SEARCH_COMPONENT);
  const copilotProps = useComponentProps(chatspotSidebarComponentDef);
  const {
    invoke: invokeCopilot
  } = useInvokeFunction(chatspotSidebarComponentDef);
  const responsiveToolbarEnabled = useIsUngated(RESPONSIVE_TOOLBAR);
  const isGridBreakpointMedium = useMedia(`screen and (max-width: ${BREAKPOINT_MEDIUM})`);
  const additionalStyles = useMemo(() => {
    return getAdditionalStyles(theme);
  }, [theme]);
  const handleKeyDown = useCallback(event => {
    if (!isToggleCopilotShortcut(event)) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    toggle();
    invokeCopilot('onCopilotToggled', {
      source: 'keyboard-shortcut'
    }).catch(captureException);
  }, [toggle, invokeCopilot]);
  useEffect(() => {
    setRootStyleProperty('--copilot-sidebar-width', isCopilotOpen ? '400px' : '0px');
  }, [isCopilotOpen]);
  useEffect(() => {
    copilotProps(current => Object.assign({}, current, {
      isSearchOpen
    }));
  });

  /** Allows users to open Copilot from any page when ?openCopilot=true is appended to the app URL */
  useEffectOnce(() => {
    /** We only run this on initial render
     * This allow users to manually close Copilot
     * when theres a query param still present
     */
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const opeCopilotQueryParam = urlParams.get('openCopilot');
      const threadIdQueryParam = urlParams.get('copilotThreadId');
      const copilotPromptQueryParam = urlParams.get('copilotPrompt');
      const openCopilotFromQueryParams = opeCopilotQueryParam && opeCopilotQueryParam === 'true' || threadIdQueryParam !== null && threadIdQueryParam.trim() !== '' || copilotPromptQueryParam !== null && copilotPromptQueryParam.trim() !== '';
      if (openCopilotFromQueryParams && !isCopilotOpen) {
        toggle();
      }
    } catch (error) {
      captureException(error);
    }
  });
  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(GlobalToolbarDivider, {}), /*#__PURE__*/_jsx(GlobalToolbarButton, {
      id: "hs-global-toolbar-copilot-list-item",
      itemName: "Copilot",
      iconName: ICON_NAMES.BREEZE_COPILOT,
      iconTextKey: responsiveToolbarEnabled && isGridBreakpointMedium ? undefined : 'nav.icons.copilot',
      ariaLabelKey: "nav.icons.copilotAriaLabel",
      onClick: () => toggle(),
      additionalStyles: additionalStyles
    })]
  });
};